/**
 * ChatterService
 * 
 * Handles mechanical switch bounce (chatter) detection and OS auto-repeat filtering.
 */
export class ChatterService extends EventTarget {
  constructor() {
    super();
    this.chatterThreshold = 50; // ms
    this.keyHistory = new Map(); // code -> { lastDown, lastUp, repeatCount }
    this.chatterLogs = [];
    this.userRepeatDelay = 250; // Default fallback
    this.userRepeatRate = 30; // Default fallback
  }

  setThreshold(ms) {
    this.chatterThreshold = ms;
  }

  calibrateRepeat(delay, rate) {
    this.userRepeatDelay = delay;
    this.userRepeatRate = rate;

    // Dynamically adjust chatter threshold to avoid false positives from fast repeats
    this.chatterThreshold = Math.max(15, Math.min(50, this.userRepeatRate - 5));
  }

  recordEvent(event) {
    const { type, code, repeat, originalEvent } = event.detail;
    const currentTime = originalEvent.timeStamp;

    if (!this.keyHistory.has(code)) {
      this.keyHistory.set(code, { lastDown: 0, lastUp: 0, repeatCount: 0, pressCount: 0 });
    }

    const history = this.keyHistory.get(code);

    if (type === 'down') {
      if (repeat) {
        history.repeatCount++;
      } else {
        // Chatter Detection — down-to-down interval only (industry standard)
        // up-to-down interval is unreliable because fast typists on short-travel
        // keyboards (laptops, low-profile) naturally produce very short up→down gaps.
        const downDiff = currentTime - history.lastDown;

        // Only flag if: interval > 0, interval < threshold, AND key was released first
        // This prevents false positives from initial key presses
        const isChatter = downDiff > 0 &&
          downDiff < this.chatterThreshold &&
          history.lastUp > history.lastDown; // Key was released before re-press

        if (isChatter && history.pressCount > 0) {
          this.flagChatter(code, downDiff);
        }

        history.lastDown = currentTime;
        history.repeatCount = 0;
        history.pressCount++;
      }
    } else if (type === 'up') {
      history.lastUp = currentTime;
    }
  }

  flagChatter(code, interval) {
    const entry = {
      code,
      interval: Math.round(interval * 10) / 10,
      timestamp: new Date().toLocaleTimeString()
    };

    this.chatterLogs.push(entry);
    this.dispatchEvent(new CustomEvent('chatterdetected', { detail: entry }));
  }

  getLogs() {
    return this.chatterLogs;
  }

  reset() {
    this.keyHistory.clear();
    this.chatterLogs = [];
  }

  clear() {
    this.reset();
  }
}

export const chatterService = new ChatterService();
